import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { ShiftCodeSegment } from '@/services/schedule/shiftCodeService';

interface SplitShiftSegmentBuilderProps {
  segments: ShiftCodeSegment[];
  onChange: (segments: ShiftCodeSegment[]) => void;
}

export function SplitShiftSegmentBuilder({
  segments,
  onChange,
}: SplitShiftSegmentBuilderProps) {
  const [errors, setErrors] = useState<string[]>([]);

  const addSegment = () => {
    if (segments.length >= 4) {
      setErrors(['Maximum 4 segments allowed']);
      return;
    }
    onChange([
      ...segments,
      { label: '', start_time: '', end_time: '' },
    ]);
    setErrors([]);
  };

  const removeSegment = (index: number) => {
    const newSegments = segments.filter((_, i) => i !== index);
    onChange(newSegments);
    validateSegments(newSegments);
  };

  const updateSegment = (index: number, field: keyof ShiftCodeSegment, value: string) => {
    const newSegments = [...segments];
    newSegments[index] = { ...newSegments[index], [field]: value };
    onChange(newSegments);
    validateSegments(newSegments);
  };

  const validateSegments = (segs: ShiftCodeSegment[]) => {
    const newErrors: string[] = [];

    // Check minimum segments
    if (segs.length < 2) {
      newErrors.push('At least 2 segments required for split shifts');
    }

    // Check for empty times
    const hasEmptyTimes = segs.some(s => !s.start_time || !s.end_time);
    if (hasEmptyTimes) {
      newErrors.push('All segments must have start and end times');
    }

    // Check for overlapping segments
    for (let i = 0; i < segs.length; i++) {
      for (let j = i + 1; j < segs.length; j++) {
        if (segs[i].start_time && segs[i].end_time && segs[j].start_time && segs[j].end_time) {
          const start1 = segs[i].start_time;
          const end1 = segs[i].end_time;
          const start2 = segs[j].start_time;
          const end2 = segs[j].end_time;

          // Check for overlap (simplified - doesn't handle overnight shifts)
          if (
            (start1 < end2 && end1 > start2) ||
            (start2 < end1 && end2 > start1)
          ) {
            newErrors.push(`Segments ${i + 1} and ${j + 1} overlap`);
          }
        }
      }
    }

    setErrors(newErrors);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base">Shift Segments</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSegment}
          disabled={segments.length >= 4}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Segment
        </Button>
      </div>

      <div className="space-y-3">
        {segments.map((segment, index) => (
          <Card key={index} className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Segment {index + 1}</span>
                </div>
                {segments.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSegment(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor={`segment-${index}-label`} className="text-xs">
                    Label (optional)
                  </Label>
                  <Input
                    id={`segment-${index}-label`}
                    placeholder="Morning"
                    value={segment.label || ''}
                    onChange={(e) => updateSegment(index, 'label', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`segment-${index}-start`} className="text-xs">
                    Start Time
                  </Label>
                  <Input
                    id={`segment-${index}-start`}
                    type="time"
                    value={segment.start_time}
                    onChange={(e) => updateSegment(index, 'start_time', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`segment-${index}-end`} className="text-xs">
                    End Time
                  </Label>
                  <Input
                    id={`segment-${index}-end`}
                    type="time"
                    value={segment.end_time}
                    onChange={(e) => updateSegment(index, 'end_time', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {errors.length > 0 && (
        <div className="text-sm text-destructive space-y-1">
          {errors.map((error, i) => (
            <div key={i}>• {error}</div>
          ))}
        </div>
      )}

      {segments.length === 0 && (
        <div className="text-center py-6 border-2 border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground mb-3">
            No segments added yet
          </p>
          <Button type="button" variant="outline" size="sm" onClick={addSegment}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Segment
          </Button>
        </div>
      )}
    </div>
  );
}
